// frontend/src/components/auditor/AuditorTax.js
// Tax reports view: tax summary from reports/tax/ and WHTB+ PDF export.

import React, { useState, useEffect, useCallback } from 'react';
import { fetchReport } from '../../utils/auditorApi';

const fmt = (v) => v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

export default function AuditorTax({ authAxios, dateRange, showFormMessage }) {
  const [taxData, setTaxData] = useState(null);
  const [whtbData, setWhtbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const params = { start_date: dateRange.startDate, end_date: dateRange.endDate };
    const [tax, whtb] = await Promise.all([
      fetchReport(authAxios, 'reports/tax/', params),
      fetchReport(authAxios, 'reports/whtb-plus/', params),
    ]);
    setTaxData(tax);
    setWhtbData(whtb);
    if (!tax && !whtb) {
      setFetchError('No tax data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const resp = await authAxios.get('reports/whtb-plus/pdf/', {
        params: { start_date: dateRange.startDate, end_date: dateRange.endDate },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `WHTB_Report_${dateRange.startDate}_to_${dateRange.endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showFormMessage?.('WHTB+ Report downloaded successfully.', false);
    } catch {
      showFormMessage?.('Failed to export WHTB+ report. Please try again.', true);
    }
    setExporting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Extract nested tax data fields
  const td = taxData || {};
  const invoiceSummary = td.invoice_summary || {};
  const whtSummary = td.wht_summary || {};
  const vatSummary = td.vat_summary || {};
  const whtbItems = whtbData?.items || whtbData?.results || (Array.isArray(whtbData) ? whtbData : []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Tax Reports</h2>
          <p className="text-sm text-gray-500 mt-1">{dateRange.startDate} to {dateRange.endDate}</p>
        </div>
        <button
          onClick={handleExportPdf}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 disabled:opacity-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {exporting ? 'Exporting…' : 'Export WHTB+ PDF'}
        </button>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Tax Summary Grid */}
      {taxData && (
        <>
          {/* Invoice / Revenue Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Invoice &amp; Revenue Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Invoiced', value: `P ${fmt(invoiceSummary.total_invoiced || td.total_invoiced_amount)}` },
                { label: 'Total Paid', value: `P ${fmt(invoiceSummary.total_paid || td.paid_invoices_amount)}` },
                { label: 'Total Outstanding', value: `P ${fmt(invoiceSummary.total_outstanding || td.outstanding_invoices_amount)}` },
                { label: 'Turnover excl Group K', value: `P ${fmt(td.total_turnover_excl_group_k)}` },
              ].map((c, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{c.label}</p>
                  <p className="text-xl font-bold text-gray-800">{c.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* WHT Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Withholding Tax (WHT)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Assessable Turnover', value: `P ${fmt(whtSummary.assessable_turnover)}` },
                { label: 'WHT Rate', value: whtSummary.wht_rate ? `${(Number(whtSummary.wht_rate) * 100).toFixed(1)}%` : '—' },
                { label: 'WHT Payable', value: `P ${fmt(whtSummary.wht_payable)}` },
                { label: 'WHT Already Paid', value: `P ${fmt(whtSummary.wht_already_paid || whtSummary.wht_paid)}` },
              ].map((c, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{c.label}</p>
                  <p className="text-xl font-bold text-gray-800">{c.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* VAT Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Value Added Tax (VAT)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Taxable Supplies', value: `P ${fmt(vatSummary.taxable_supplies)}` },
                { label: 'VAT Rate', value: vatSummary.vat_rate ? `${(Number(vatSummary.vat_rate) * 100).toFixed(1)}%` : '—' },
                { label: 'Output VAT', value: `P ${fmt(vatSummary.output_vat)}` },
                { label: 'Net VAT Payable', value: `P ${fmt(vatSummary.net_vat_payable)}` },
              ].map((c, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{c.label}</p>
                  <p className="text-xl font-bold text-gray-800">{c.value}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!taxData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-gray-500">No tax data available for the selected period.</p>
        </div>
      )}

      {/* WHTB+ line items */}
      {whtbItems.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800">WHTB+ Line Items ({whtbItems.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">Invoice #</th>
                  <th className="px-4 py-3 text-right">Gross</th>
                  <th className="px-4 py-3 text-right">WHT</th>
                  <th className="px-4 py-3 text-right">Net</th>
                  <th className="px-4 py-3 text-left">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {whtbItems.slice(0, 50).map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-800">{item.client_name || item.client || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{item.invoice_number || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-800">{fmt(item.gross_amount || item.amount)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{fmt(item.wht_amount || item.wht)}</td>
                    <td className="px-4 py-3 text-right text-green-700">{fmt(item.net_amount || item.net)}</td>
                    <td className="px-4 py-3 text-gray-600">{item.date ? new Date(item.date).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {whtbItems.length > 50 && (
            <div className="px-6 py-3 bg-gray-50 text-center text-sm text-gray-500">
              Showing first 50 of {whtbItems.length} items. Export the full WHTB+ PDF for complete data.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
